import { api } from '../lib/api'

export type ModQueueUser = {
  id: string
  username?: string
  email?: string
}

export type ModQueueDecision = {
  action: string
  categories: string[]
  confidence: string | number
  severity: number
  rationale_short?: string
  rationale_internal?: string
  decided_by?: string
  created_at?: string
}

export type SignalSummary = {
  rules_count: number
  domains: string[]
  trust_score?: number | null
  trust_tier?: string | null
}

export type Signals = {
  rules: Array<{ id: string; matches: string[]; weight: number }>
  links: Array<{ url: string; domain?: string; flags?: string[] }>
  trust: { score?: number | null; tier?: string | null; factors?: Record<string, any> }
  similarity: { simhash?: string | null; phash?: string | null; near_duplicates?: number }
  notes: string[]
}

export type ModQueueEvent = {
  event_id: string
  content_type: string
  content_id: string
  revision: number
  actor: ModQueueUser | null
  created_at: string
  queue: string
  status: string
  assigned_to: ModQueueUser | null
  current_decision: ModQueueDecision | null
  signal_summary?: SignalSummary
  text_excerpt: string
  links_count: number
  media_count: number
  reports_count: number
  latest_reported_at?: string | null
  appeals_count: number
  latest_appealed_at?: string | null
}

export type ModQueueEventDetail = ModQueueEvent & {
  text: string
  links: Array<Record<string, any>>
  media: Array<Record<string, any>>
  context: Record<string, any>
  fingerprints: Record<string, any>
  link_domains?: string[]
  signals?: Signals
  reports?: Array<{
    id: string
    created_at: string
    reason: string
    note?: string
    reporter_id?: string | null
  }>
  appeals?: Array<{
    id: string
    created_at: string
    status: string
    message: string
    reviewed_at?: string | null
    reviewed_by?: ModQueueUser | null
    staff_note?: string
    user_note?: string
  }>
  audit?: Array<{
    id: string
    created_at: string
    actor?: ModQueueUser | null
    action_type: string
    summary?: string
  }>
  resolution: string
  resolution_note: string
  claimed_at: string | null
  resolved_at: string | null
}

export type ModQueueListParams = {
  queue?: string
  status?: string
  content_type?: string
  assigned?: 'me' | 'unassigned'
  q?: string
}

export type ModQueueListResult = {
  items: ModQueueEvent[]
  next?: string | null
  count?: number
}

export async function listModqueueEvents(
  params?: ModQueueListParams,
  pageUrl?: string
): Promise<ModQueueListResult> {
  const url = pageUrl || '/api/moderation/modqueue/events'
  const { data } = await api.get(url, pageUrl ? undefined : { params })
  if (Array.isArray(data)) {
    return { items: data }
  }
  return {
    items: data.results ?? [],
    next: data.next ?? null,
    count: data.count,
  }
}

export async function getModqueueEvent(eventId: string): Promise<ModQueueEventDetail> {
  const { data } = await api.get<ModQueueEventDetail>(`/api/moderation/modqueue/events/${eventId}`)
  return data
}

export async function claimModqueueEvent(eventId: string) {
  const { data } = await api.post(`/api/moderation/modqueue/events/${eventId}/claim`)
  return data
}

export async function releaseModqueueEvent(eventId: string, force = false) {
  const { data } = await api.post(`/api/moderation/modqueue/events/${eventId}/release`, null, {
    params: force ? { force: 1 } : undefined,
  })
  return data
}

export type ModQueueResolvePayload = {
  final_action: string
  categories?: string[]
  severity: number
  confidence: number
  resolution: 'upheld' | 'overridden' | 'dismissed'
  resolution_note?: string
  rationale_short?: string
  rationale_internal?: string
}

export async function resolveModqueueEvent(eventId: string, payload: ModQueueResolvePayload) {
  const { data } = await api.post(`/api/moderation/modqueue/events/${eventId}/resolve`, payload)
  return data
}

export type ModQueueRelatedResult = {
  items: ModQueueEvent[]
  meta?: { limit?: number; count?: number }
}

export async function getRelatedActor(eventId: string): Promise<ModQueueRelatedResult> {
  const { data } = await api.get(`/api/moderation/modqueue/events/${eventId}/related/actor`)
  return data
}

export async function getRelatedDomains(eventId: string): Promise<ModQueueRelatedResult> {
  const { data } = await api.get(`/api/moderation/modqueue/events/${eventId}/related/domains`)
  return data
}

export async function getRelatedRevisions(eventId: string): Promise<ModQueueRelatedResult> {
  const { data } = await api.get(`/api/moderation/modqueue/events/${eventId}/related/revisions`)
  return data
}

export async function getRelatedSimhash(eventId: string): Promise<ModQueueRelatedResult> {
  const { data } = await api.get(`/api/moderation/modqueue/events/${eventId}/related/simhash`)
  return data
}

export type ModQueueBulkResolvePayload = ModQueueResolvePayload & {
  event_ids: string[]
}

export async function bulkResolveEvents(payload: ModQueueBulkResolvePayload) {
  const { data } = await api.post(`/api/moderation/modqueue/events/bulk_resolve`, payload)
  return data
}

export type ReportPayload = {
  content_type: 'post' | 'comment' | 'profile' | 'listing'
  content_id: string
  reason: 'spam' | 'scam' | 'impersonation' | 'harassment' | 'adult' | 'minor_safety' | 'violence' | 'other'
  note?: string
  context?: Record<string, any>
}

export async function reportContent(payload: ReportPayload) {
  const { data } = await api.post(`/api/moderation/report`, payload)
  return data
}

export type AppealPayload = {
  content_type: 'post' | 'comment' | 'profile' | 'listing'
  content_id: string
  message: string
}

export type AppealItem = {
  appeal_id: string
  event_id?: string | null
  content_type: string
  content_id: string
  created_at: string
  status: string
  reviewed_at?: string | null
  user_note?: string | null
}

export async function createAppeal(payload: AppealPayload) {
  const { data } = await api.post(`/api/moderation/appeal`, payload)
  return data
}

export async function listMyAppeals(): Promise<{ items: AppealItem[] }> {
  const { data } = await api.get(`/api/moderation/appeals/mine`)
  return data
}

export async function reviewAppeal(appealId: string, payload: { status: 'accepted' | 'denied'; staff_note?: string; user_note?: string }) {
  const { data } = await api.post(`/api/moderation/modqueue/appeals/${appealId}/review`, payload)
  return data
}

export type ModerationOverviewStats = {
  window_days: number
  kpis: {
    events_24h: number
    events_7d: number
    ai_decided_7d: number
    sent_to_human_7d: number
    human_resolved_7d: number
    reports_24h: number
    reports_7d: number
    appeals_24h: number
    appeals_7d: number
  }
  by_content_type: Array<{ key: string; count: number }>
  by_action: Array<{ key: string; count: number }>
  by_queue: Array<{ key: string; count: number }>
  top_rules_24h: Array<{ id: string; count: number }>
  top_domains_24h: Array<{ domain: string; count: number }>
  top_actors_24h: Array<{ actor_id: number; username?: string | null; count: number }>
  queue_open: { human_review: number; urgent: number; total: number }
}

export async function getModerationOverviewStats(days = 7): Promise<ModerationOverviewStats> {
  const { data } = await api.get<ModerationOverviewStats>(`/api/moderation/stats/overview`, {
    params: { days },
  })
  return data
}

export type PolicyVersionSummary = {
  id: string
  version: number
  is_active: boolean
  created_at: string
}

export type PolicySummary = {
  key: string
  name: string
  description: string
  active_version: number | null
  versions: PolicyVersionSummary[]
}

export type PolicyVersionDetail = {
  id: string
  policy_key: string
  version: number
  text: string
  config: Record<string, any>
  is_active: boolean
  created_at: string
}

export type PolicyDryRunPayload = {
  content_type: 'post' | 'comment' | 'profile' | 'listing' | 'handle'
  actor_user_id?: string
  text?: string
  context?: Record<string, any>
  links?: Array<{ url: string }>
  media?: Array<Record<string, any>>
  fingerprints?: Record<string, any>
}

export async function listPolicies(): Promise<PolicySummary[]> {
  const { data } = await api.get<PolicySummary[]>(`/api/moderation/policies`)
  return data
}

export async function getPolicyVersion(key: string, versionId: string): Promise<PolicyVersionDetail> {
  const { data } = await api.get<PolicyVersionDetail>(`/api/moderation/policies/${key}/versions/${versionId}`)
  return data
}

export async function createPolicyVersion(
  key: string,
  payload: { text?: string; config?: Record<string, any> }
) {
  const { data } = await api.post(`/api/moderation/policies/${key}/versions`, payload)
  return data
}

export async function activatePolicyVersion(key: string, versionId: string) {
  const { data } = await api.post(`/api/moderation/policies/${key}/versions/${versionId}/activate`)
  return data
}

export async function dryRunPolicy(payload: PolicyDryRunPayload) {
  const { data } = await api.post(`/api/moderation/policies/dry_run`, payload)
  return data
}

export async function seedPolicies() {
  const { data } = await api.post(`/api/moderation/policies/seed`)
  return data
}

export type PolicyAuditItem = {
  id: string
  action_type: string
  created_at: string
  actor_id?: number | null
  actor_username?: string | null
  policy_key?: string | null
  version?: number | null
}

export async function listPolicyAudit(limit = 10): Promise<{ items: PolicyAuditItem[] }> {
  const { data } = await api.get(`/api/moderation/policies/audit`, { params: { limit } })
  return data
}

export type ImageModerationQueueItem = {
  id: string
  content_type: string
  object_type: string
  object_id: string
  target_field: string
  status: string
  decision: string
  decision_reason?: string
  categories?: string[]
  scores?: Record<string, number>
  provider?: string
  model?: string
  thumbnail_url?: string
  created_at: string
}

export async function listImageModerationQueue(params?: { type?: string; page?: number; page_size?: number }) {
  const { data } = await api.get<{ count: number; page: number; page_size: number; results: ImageModerationQueueItem[] }>(
    '/api/admin/moderation/queue',
    { params },
  )
  return data
}

export async function approveImageModerationCheck(checkId: string) {
  const { data } = await api.post(`/api/admin/moderation/${checkId}/approve`)
  return data
}

export async function blockImageModerationCheck(checkId: string) {
  const { data } = await api.post(`/api/admin/moderation/${checkId}/block`)
  return data
}

export type ModerationCheckStatus = {
  id: string
  check_id: string
  content_type: string
  status: 'pending' | 'passed' | 'held' | 'blocked' | 'error' | string
  decision: 'auto_approve' | 'hold_for_review' | 'block' | string
  decision_reason?: string
  provider?: string
  model?: string
  created_at?: string
  updated_at?: string
}

export async function getModerationCheckStatus(checkId: string): Promise<ModerationCheckStatus> {
  const { data } = await api.get<ModerationCheckStatus>(`/api/moderation/checks/${checkId}/`)
  return data
}
